import { CLabel, CTextarea } from "@coreui/react"
import { NoDataAndWentWrongHandler } from "components/common/noData"
import { Table } from "components/common/table"
import {
  crNotificationHeaders,
  getRedirectionLinkCR,
  prodSnowHost
} from "constants/Constants"
import { getCrDetails } from "extensions/usoc/api/service"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import useSWR from "swr"
import { useExtension, useUserAuth } from "utils"
import { isAnyAPIFailed } from "utils/nodata"
import { retryFailedRequests } from "utils/retryFailedRequests"

import { CMSButton } from "../button"
import CMSTypography, { fontSizes } from "../typography/CMSTypography"

async function fetchCrDetails(auth, extension, notifications) {
  const host = extension?.config?.env?.clientApiGateway?.URL
   if(notifications.projectId==="PROJ0040466" || notifications.projectId==="PROJ0040017"){

  const results = []
  for (const notification of notifications) {

    const result = await retryFailedRequests(
      { auth, host },
      notification.project_id,
      `CHG_NUMBER=${notification?.ticket_number}`,
      getCrDetails
    )
    results.push(result)
  }
  return results
}else{
    const promises = []
  for (const notification of notifications) {
    promises.push(
      getCrDetails(
        { auth, host },
        notification.project_id,
        `CHG_NUMBER=${notification?.ticket_number}`
      ).catch((err) => {
        throw err
      })
    )
  }
  const results = await Promise.all(promises)
  return results
}
}

/**
 * Component render Notofication table for Change Request under Notification Drawer
 *
 *
 * @example
 * const crNotifications = [
 * {
 *   sys_id: "8509380e1be270101683c913604bcb54",
 *   project_id: "PROJ0037154",
 *   ticket_number: "CHG0253454"
 * }]
 * return(
 * <NotificationCRTable
 *  notifications={crNotifications}
 *  onApproveClick={onApproveClick}
 * />)
 */
export default function NotificationCRTable({ notifications, onApproveClick }) {
  const auth = useUserAuth()

  const [expandedRow, setExpandedRow] = useState([])
  const [comments, setComments] = useState("")
  const [crData, setCrData] = useState([])

  const extension = useExtension()

  // Making api call for getting data about Change Request notifications
  const { data: crResult } = useSWR(
    () =>
      Array.isArray(notifications) && notifications.length
        ? notifications?.map(
          (notification) =>
            `/projects/${notification.project_id}/get_change_request_details?CHG_NUMBER=${notification?.ticket_number}`
        )
        : null,
    () => fetchCrDetails(auth, extension, notifications),
    { suspense: true }
  )

  const userRole = useSelector((state) => state.user.role)

  useEffect(() => {
    if (crResult) {
      setCrData(
        crResult.reduce((acc, element) => {
          if (element.data[0]?.chg_sys_id) {
            acc.push(element.data[0])
          }
          return acc
        }, [])
      )
    }
  }, [crResult])

  // To handle the expansion of particular row(either we want want toexpand row or close the expanded row)
  function toggleDetails(id) {
    setExpandedRow((prevExpRow) => (prevExpRow === id ? "" : id))
    setComments("")
  }

  const data = crData.map((data) => {
    return {
      _key: data.chg_sys_id,
      _onClick: () => {
        toggleDetails(data.chg_sys_id)
      },
      _expandedRow:
        expandedRow === data.chg_sys_id ? (
          <td colSpan={12} className="noti-extra-details">
            <div className="d-flex flex-column mx-3 mb-3">
              <div className="d-flex flex-row">
                <span className="font-weight-bold">CREATED DATE: </span>
                <span className="ml-2">{data.chg_opened_at}</span>
              </div>
              <div className="noti-feedback-comment">
                <div className="d-flex flex-row h-100 mt-3">
                  <CLabel className="font-weight-bold mr-2">COMMENTS: </CLabel>
                  <CTextarea
                    placeholder="Pls write your Comments here!"
                    className="text-area w-100 mr-5"
                    onChange={(e) => setComments(e.target.value)}
                    value={comments}
                  />
                </div>
                <div className="d-flex flex-column-reverse col-md-8 align-items-end">
                  <div className="d-flex flex-row pt-3">
                    <CMSButton
                      type="warning"
                      onClick={() =>
                        onApproveClick(
                          "rejected",
                          comments,
                          data.chg_sys_id,
                          "cr"
                        )
                      }
                      disabled={comments === ""}
                      className="mr-3"
                    >
                      Reject
                    </CMSButton>
                    <CMSButton
                      type="primary"
                      onClick={() =>
                        onApproveClick(
                          "approved",
                          comments,
                          data.chg_sys_id,
                          "cr"
                        )
                      }
                    >
                      Approve
                    </CMSButton>
                  </div>
                  <span>Note: Please Comment Before You Reject a Request</span>
                </div>
              </div>
            </div>
          </td>
        ) : null,
      change_number: {
        text: data.change_number,
        ele: (
          <CMSTypography
            fontSize={fontSizes.small}
            clickable
            href={
              getRedirectionLinkCR(userRole, prodSnowHost) + data?.chg_sys_id
            }
            onClick={(e) => e?.stopPropagation()}
          >
            {data.change_number}
          </CMSTypography>
        )
      },
      project_name: { text: data.project_name },
      short_description: { text: data.short_description },
      chg_opened_by: { text: data.chg_opened_by },
      chg_start_date: { text: data.chg_start_date },
      chg_end_date: { text: data.chg_end_date },
      change_state: { text: data.change_state },
      notes: { text: data.notes }
    }
  })
  return (
    <NoDataAndWentWrongHandler wentWrongCondition={isAnyAPIFailed(crResult)}>
      <Table
        headers={crNotificationHeaders}
        data={data}
        disableSearch
        dataTestId="cr-notifications"
        disableExport
        disablePagination
      />
    </NoDataAndWentWrongHandler>
  )
}

NotificationCRTable.propTypes = {
  notifications: PropTypes.array,
  onApproveClick: PropTypes.func
}
